import ansiEscapes from "ansi-escapes"
import {
  createPrompt,
  isDownKey,
  isEnterKey,
  isNumberKey,
  isSpaceKey,
  isUpKey,
  makeTheme,
  Separator,
  useKeypress,
  useMemo,
  usePagination,
  usePrefix,
  useRef,
  useState,
  ValidationError,
} from "@inquirer/core"
import figures from "@inquirer/figures"
import colors from "yoctocolors-cjs"

type Choice<Value> = {
  checked?: boolean
  description?: string
  disabled?: boolean | string
  name?: string
  short?: string
  value: Value
}

type NormalizedChoice<Value> = {
  checked: boolean
  description?: string
  disabled: boolean | string
  name: string
  short: string
  value: Value
}

type CheckboxTheme = {
  helpMode: "always" | "never" | "auto"
  icon: {
    checked: string
    cursor: string
    unchecked: string
  }
  style: {
    description: (text: string) => string
    disabledChoice: (text: string) => string
    renderSelectedChoices: <T>(
      selectedChoices: ReadonlyArray<NormalizedChoice<T>>,
      allChoices: ReadonlyArray<NormalizedChoice<T> | Separator>,
    ) => string
  }
}

const checkboxTheme: CheckboxTheme = {
  icon: {
    checked: colors.green(figures.circleFilled),
    unchecked: figures.circle,
    cursor: figures.pointer,
  },
  style: {
    disabledChoice: text => colors.dim(`- ${text}`),
    renderSelectedChoices: selectedChoices => selectedChoices.map(choice => choice.short).join(", "),
    description: text => colors.blue(text),
  },
  helpMode: "auto",
}

function isSelectable<Value>(item: NormalizedChoice<Value> | Separator): item is NormalizedChoice<Value> {
  return !Separator.isSeparator(item) && !item.disabled
}

function isChecked<Value>(item: NormalizedChoice<Value> | Separator): item is NormalizedChoice<Value> {
  return isSelectable(item) && Boolean(item.checked)
}

function toggle<Value>(item: NormalizedChoice<Value> | Separator): NormalizedChoice<Value> | Separator {
  return isSelectable(item) ? { ...item, checked: !item.checked } : item
}

function check<Value>(checked: boolean) {
  return function (item: NormalizedChoice<Value> | Separator): NormalizedChoice<Value> | Separator {
    return isSelectable(item) ? { ...item, checked } : item
  }
}

function normalizeChoices<Value>(choices: readonly (Separator | Choice<Value>)[]): Array<NormalizedChoice<Value> | Separator> {
  return choices.map(choice => {
    if (Separator.isSeparator(choice)) {
      return choice
    }

    const name = choice.name ?? String(choice.value)
    return {
      value: choice.value,
      name,
      short: choice.short ?? name,
      disabled: choice.disabled ?? false,
      checked: choice.checked ?? false,
      description: choice.description,
    }
  })
}

const implicitCheckbox = createPrompt(<Value>(
  config: {
    choices: readonly (Separator | Choice<Value>)[]
    instructions?: string | boolean
    loop?: boolean
    message: string
    pageSize?: number
    required?: boolean
    shortcuts?: {
      all?: string | null
      invert?: string | null
    }
    validate?: (choices: readonly Choice<Value>[]) => boolean | string | Promise<string | boolean>
  },
  done: (value: Value[]) => void,
) => {
  const { instructions, pageSize = 7, loop = true, required, validate = () => true } = config
  const shortcuts = { all: "a", invert: "i", ...config.shortcuts }
  const theme = makeTheme(checkboxTheme, undefined)
  const firstRender = useRef(true)
  const [status, setStatus] = useState<"idle" | "done">("idle")
  const [submittedItems, setSubmittedItems] = useState<Array<NormalizedChoice<Value>>>([])
  const prefix = usePrefix({ status, theme })
  const [items, setItems] = useState(normalizeChoices(config.choices))
  const bounds = useMemo(() => {
    const first = items.findIndex(item => isSelectable(item))
    const last = items.findLastIndex(item => isSelectable(item))
    if (first === -1) {
      throw new ValidationError("[checkbox prompt] No selectable choices. All choices are disabled.")
    }

    return { first, last }
  }, [items])
  const [active, setActive] = useState(bounds.first)
  const [showHelpTip, setShowHelpTip] = useState(true)
  const [errorMsg, setError] = useState<string>()

  useKeypress(async key => {
    if (isEnterKey(key)) {
      const currentSelection = items.filter(item => isChecked(item))
      const implicitSelection =
        currentSelection.length === 0 && isSelectable(items[active]) ? [items[active]] : currentSelection
      const isValid = await validate([...implicitSelection])

      if (required && implicitSelection.length === 0) {
        setError("At least one choice must be selected")
      } else if (isValid === true) {
        setSubmittedItems(implicitSelection)
        setStatus("done")
        done(implicitSelection.map(choice => choice.value))
      } else {
        setError(isValid || "You must select a valid value")
      }

    } else if (isUpKey(key) || isDownKey(key)) {
      if (loop || (isUpKey(key) && active !== bounds.first) || (isDownKey(key) && active !== bounds.last)) {
        const offset = isUpKey(key) ? -1 : 1
        let next = active
        do {
          next = (next + offset + items.length) % items.length
        } while (!isSelectable(items[next]))

        setActive(next)
      }
    } else if (isSpaceKey(key)) {
      setError(undefined)
      setShowHelpTip(false)
      setItems(items.map((choice, index) => (index === active ? toggle(choice) : choice)))
    } else if (key.name === shortcuts.all) {
      const selectAll = items.some(choice => isSelectable(choice) && !choice.checked)
      setItems(items.map(check(selectAll)))
    } else if (key.name === shortcuts.invert) {
      setItems(items.map(choice => toggle(choice)))
    } else if (isNumberKey(key)) {
      const position = Number(key.name) - 1
      const item = items[position]
      if (item !== undefined && isSelectable(item)) {
        setActive(position)
        setItems(items.map((choice, index) => (index === position ? toggle(choice) : choice)))
      }
    }
  })

  const message = theme.style.message(config.message, status)
  let description: string | undefined
  const page = usePagination({
    items,
    active,
    renderItem({ item, isActive }) {
      if (Separator.isSeparator(item)) {
        return ` ${item.separator}`
      }

      if (item.disabled) {
        const disabledLabel = typeof item.disabled === "string" ? item.disabled : "(disabled)"
        return theme.style.disabledChoice(`${item.name} ${disabledLabel}`)
      }

      if (isActive) {
        description = item.description
      }

      const checkbox = item.checked ? theme.icon.checked : theme.icon.unchecked
      const color = isActive ? theme.style.highlight : (text: string) => text
      const cursor = isActive ? theme.icon.cursor : " "
      return color(`${cursor}${checkbox} ${item.name}`)
    },
    pageSize,
    loop,
  })

  if (status === "done") {
    const answer = theme.style.answer(theme.style.renderSelectedChoices(submittedItems, items))
    return `${prefix} ${message} ${answer}`
  }

  let helpTipTop = ""
  let helpTipBottom = ""
  if (
    theme.helpMode === "always" ||
    (theme.helpMode === "auto" && showHelpTip && (instructions === undefined || instructions))
  ) {
    if (typeof instructions === "string") {
      helpTipTop = instructions
    } else {
      const keys = [
        `${theme.style.key("space")} to select`,
        shortcuts.all ? `${theme.style.key(shortcuts.all)} to toggle all` : "",
        shortcuts.invert ? `${theme.style.key(shortcuts.invert)} to invert selection` : "",
        `and ${theme.style.key("enter")} to proceed`,
      ]
      helpTipTop = ` (Press ${keys.filter(key => key !== "").join(", ")})`
    }

    if (
      items.length > pageSize &&
      (theme.helpMode === "always" || (theme.helpMode === "auto" && firstRender.current))
    ) {
      helpTipBottom = `\n${theme.style.help("(Use arrow keys to reveal more options)")}`
      firstRender.current = false
    }
  }

  const choiceDescription = description ? `\n${theme.style.description(description)}` : ""
  const error = errorMsg ? `\n${theme.style.error(errorMsg)}` : ""

  return `${prefix} ${message}${helpTipTop}\n${page}${helpTipBottom}${choiceDescription}${error}${ansiEscapes.cursorHide}`
})

export async function promptImplicitCheckbox<Value>(config: {
  choices: readonly Choice<Value>[]
  message: string
}): Promise<Value[]> {
  return implicitCheckbox({
    choices: config.choices,
    message: config.message,
    loop: false,
    required: true,
  })
}
